import { type V1ApiResponse } from '@webapp/core/interfaces/api/V1ApiResponse'
import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type RepoPaginationResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginationResponse'
import {
  HttpError,
  type IHttpClient,
} from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type ITravelRepository } from '@webapp/modules/expense/business-logic/interfaces/ITravelRepository'
import { type TripFormValue } from '@webapp/modules/expense/components/TravelTripForm/types'
import { type TravelDestination } from '@webapp/modules/expense/entities/TravelDestination'
import { type TravelRequestDetailConstructor } from '@webapp/modules/expense/entities/TravelRequestDetail'
import { type TravelRequestSummaryConstructor } from '@webapp/modules/expense/entities/TravelRequestSummary'
import { type WatchlistApproverConstructor } from '@webapp/modules/expense/entities/WatchlistApprover'
import { type CreateTravelRequestFormSchema } from '@webapp/modules/expense/features/create-travel-request/views/TravelRequestForm/TravelRequestForm'
import { type CreateTravelRequestPayload } from '../../api-models/CreateTravelRequestPayload'
import { type TravelRequestDetailApiModel } from '../../api-models/TravelRequestDetailApiModel'
import {
  type GetApprovableTravelsResponse,
  type GetRequestedTravelsResponse,
} from '../../api-models/TravelRequestSummaryApiModel'
import { type UpdateTravelDestinationPayload } from '../../api-models/UpdateTravelDestinationPayload'
import { type UpdateTravelRequestPayload } from '../../api-models/UpdateTravelRequestPayload'
import { type WatchlistApproverCountResponseApiModel } from '../../api-models/WatchlistApproverOptionApiModel'
import { mapCreateTravelRequestFormToPayload } from '../../mappers/mapCreateTravelRequestFormToPayload'
import { mapEditTripFormToPayload } from '../../mappers/mapEditTripFormToPayload'
import { mapTravelRequestDetailApiModel } from '../../mappers/mapTravelRequestDetailApiModel'
import { mapTravelRequestSummaryApiModel } from '../../mappers/mapTravelRequestSummaryApiModel'
import { mapWatchlistApproverOptionApiModel } from '../../mappers/mapWatchlistApproverOptionApiModel'
import {
  type TravelListStatus,
  type TravelStatusCounts,
} from '../../types/travel'

interface Dependency {
  httpClient: IHttpClient
}

interface V1ErrorData {
  __all__?: string[]
  [key: string]: string[] | undefined
}

// v1 endpoints (`/api/travel/*`) signal failure via HTTP errors carrying a
// `{ success: false, data: { __all__: [...] } }` body (ADR-4).
const throwRepoErrorFromApiV1Error = (error: unknown): never => {
  if (error instanceof RepoError) throw error

  if (error instanceof HttpError) {
    // `HttpError.response` is the raw v1 error body.
    const errorData = (error.response as { data?: V1ErrorData } | undefined)
      ?.data

    // Only carry an actual API-provided envelope message. A non-JSON failure
    // (e.g. a 500 HTML page) has no envelope, and the generic
    // `HTTP Error NNN` string must not surface in user-facing toasts —
    // consumers fall back to their documented error copy (BHV-012).
    throw new RepoError({
      code: null,
      message: errorData?.__all__?.[0] ?? '',
      status: error.status,
      response: error.response,
      headers: error.headers,
    })
  }

  throw error
}

interface ApprovalErrorEnvelope {
  errors_with_codes?: { code?: string; message?: string }[]
}

// The bulk approve/deny endpoints (`/api/v2/approve|deny/travel`) signal
// failure via HTTP 400 carrying an `errors_with_codes` envelope — the first
// entry's message is the user-facing copy (ADR-4/ADR-10).
const throwRepoErrorFromApprovalError = (error: unknown): never => {
  if (error instanceof RepoError) throw error

  if (error instanceof HttpError) {
    const envelope = error.response as ApprovalErrorEnvelope | undefined

    throw new RepoError({
      code: null,
      message: envelope?.errors_with_codes?.[0]?.message ?? '',
      status: error.status,
      response: error.response,
      headers: error.headers,
    })
  }

  throw error
}

// v2/v3 endpoints carry no user-facing envelope message on failure — the
// RepoError message stays empty so consumers fall back to their documented
// toast copy via `getMessageFromError(error) || fallback`.
const throwRepoErrorFromHttpError = (error: unknown): never => {
  if (error instanceof RepoError) throw error

  if (error instanceof HttpError) {
    throw new RepoError({
      code: null,
      message: '',
      status: error.status,
      response: error.response,
      headers: error.headers,
    })
  }

  throw error
}

// Bulk approve payload item — snake_case DTO stays inside the data-access
// layer (`approver_id: null` = final approval).
interface ApproveTravelPayloadItem {
  travel_id: number
  approver_id: Nullable<number>
  force_approve: boolean
}

export class TravelRepository implements ITravelRepository {
  constructor(private readonly deps: Dependency) {}

  async listApprovable(
    status: TravelListStatus,
    params: RepoFilterParams & { subscribedApproverId?: number }
  ): Promise<{
    data: TravelRequestSummaryConstructor[]
    counts: TravelStatusCounts
    pagination: RepoPaginationResponse
  }> {
    try {
      const { subscribedApproverId, ...filterParams } = params

      const res = await this.deps.httpClient.get<GetApprovableTravelsResponse>(
        `/api/v2/travels/approvable/${status}/`,
        {
          params: {
            ...formatRepoFiltersToApiFilters(filterParams),
            counts: true,
            ...(subscribedApproverId === undefined
              ? {}
              : { subscribed: subscribedApproverId }),
          },
        }
      )

      const metadata = res.data.metadata
      // With the `subscribed` watchlist filter the backend returns a FLAT
      // `metadata.counts` object instead of `counts.approvable` (BHV-031).
      const rawCounts = metadata?.counts?.approvable ?? metadata?.counts

      return {
        data: res.data.data.map(mapTravelRequestSummaryApiModel),
        counts: {
          all: rawCounts?.all ?? 0,
          pending: rawCounts?.pending ?? 0,
          approved: rawCounts?.approved ?? 0,
          denied: rawCounts?.denied ?? 0,
        },
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      return throwRepoErrorFromHttpError(err)
    }
  }

  // Consumed only by the ADR-3 `WatchlistApproverSelect` `loadOptions` —
  // mirrors `ExpenseRepository.listWatchlistApprovers` with `object_type=travel`.
  async listWatchlistApprovers(
    params: RepoFilterParams
  ): Promise<RepoPaginatedResponse<WatchlistApproverConstructor>> {
    try {
      const res =
        await this.deps.httpClient.get<WatchlistApproverCountResponseApiModel>(
          '/api/v2/settings/approval-subscribed/approver/count/',
          {
            params: {
              object_type: 'travel',
              ...formatRepoFiltersToApiFilters(params),
            },
          }
        )

      const paginationRaw = res.data.metadata?.pagination

      return {
        data: (res.data.data ?? []).map(mapWatchlistApproverOptionApiModel),
        pagination: {
          count: paginationRaw?.count ?? 0,
          pageSize: paginationRaw?.page_size ?? 20,
          numPages: paginationRaw?.num_pages ?? 1,
          page: paginationRaw?.current_page ?? 1,
        },
      }
    } catch (err) {
      return throwRepoErrorFromHttpError(err)
    }
  }

  async getByUuid(uuid: string): Promise<TravelRequestDetailConstructor> {
    try {
      const res = await this.deps.httpClient.get<
        V1ApiResponse<TravelRequestDetailApiModel>
      >(`/api/travel/${uuid}/`)

      // HTTP 200 can still signal failure in the v1 envelope.
      if (res.data.success === false) {
        throw new RepoError({
          code: null,
          message: res.data.description,
          status: null,
          response: res.data,
        })
      }

      return mapTravelRequestDetailApiModel(res.data.data)
    } catch (err) {
      return throwRepoErrorFromApiV1Error(err)
    }
  }

  async update(
    travelId: number,
    payload: UpdateTravelRequestPayload
  ): Promise<void> {
    try {
      await this.deps.httpClient.put<void, UpdateTravelRequestPayload>(
        `/api/v3/travels/${travelId}/`,
        payload
      )
    } catch (err) {
      throwRepoErrorFromHttpError(err)
    }
  }

  async updateDestination(
    destination: TravelDestination,
    trip: TripFormValue
  ): Promise<void> {
    try {
      const payload = mapEditTripFormToPayload(trip, destination)

      await this.deps.httpClient.put<void, UpdateTravelDestinationPayload>(
        `/api/v3/travels/destinations/${destination.id}/`,
        payload
      )
    } catch (err) {
      throwRepoErrorFromHttpError(err)
    }
  }

  async deleteDestination(destinationId: number): Promise<void> {
    try {
      // 204 No Content is success.
      await this.deps.httpClient.delete<void>(
        `/api/v3/travels/destinations/${destinationId}/`
      )
    } catch (err) {
      throwRepoErrorFromHttpError(err)
    }
  }

  async delete(travelId: number): Promise<void> {
    try {
      // 204 No Content is success.
      await this.deps.httpClient.delete<void>(`/api/v3/travels/${travelId}/`)
    } catch (err) {
      throwRepoErrorFromHttpError(err)
    }
  }

  // Serves both single (one-element array) and bulk approve.
  async approve(
    items: {
      travelId: number
      approverId: Nullable<number>
      forceApprove?: boolean
    }[]
  ): Promise<void> {
    try {
      await this.deps.httpClient.post<void, ApproveTravelPayloadItem[]>(
        '/api/v2/approve/travel',
        items.map((item) => ({
          travel_id: item.travelId,
          approver_id: item.approverId,
          force_approve: item.forceApprove ?? false,
        }))
      )
    } catch (err) {
      throwRepoErrorFromApprovalError(err)
    }
  }

  // Serves both single and bulk deny.
  async deny(travelIds: number[]): Promise<void> {
    try {
      await this.deps.httpClient.post<void, { travel_id: number }[]>(
        '/api/v2/deny/travel',
        travelIds.map((travelId) => ({ travel_id: travelId }))
      )
    } catch (err) {
      throwRepoErrorFromApprovalError(err)
    }
  }

  async listRequested(
    status: TravelListStatus,
    params: RepoFilterParams
  ): Promise<{
    data: TravelRequestSummaryConstructor[]
    counts: TravelStatusCounts
    pagination: RepoPaginationResponse
  }> {
    try {
      const res = await this.deps.httpClient.get<GetRequestedTravelsResponse>(
        `/api/v2/travels/requested/${status}/`,
        {
          params: {
            ...formatRepoFiltersToApiFilters(params),
            counts: true,
          },
        }
      )

      const metadata = res.data.metadata

      return {
        data: res.data.data.map(mapTravelRequestSummaryApiModel),
        counts: {
          all: metadata?.counts?.requested?.all ?? 0,
          pending: metadata?.counts?.requested?.pending ?? 0,
          approved: metadata?.counts?.requested?.approved ?? 0,
          denied: metadata?.counts?.requested?.denied ?? 0,
        },
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      return throwRepoErrorFromHttpError(err)
    }
  }

  async remindApprover(
    travelId: number,
    message: Nullable<string>
  ): Promise<void> {
    try {
      // HTTP 201 — the http client resolves for any 2xx.
      await this.deps.httpClient.post<void, { message: Nullable<string> }>(
        `/api/v3/travels/${travelId}/remind-approver/`,
        { message: message || null }
      )
    } catch (err) {
      throwRepoErrorFromHttpError(err)
    }
  }

  async create(
    formValues: CreateTravelRequestFormSchema,
    trips: TripFormValue[]
  ): Promise<{ id: number }> {
    try {
      const payload = mapCreateTravelRequestFormToPayload(formValues, trips)

      const res = await this.deps.httpClient.post<
        V1ApiResponse<number>,
        CreateTravelRequestPayload
      >('/api/travel/request/', payload)

      // HTTP 200 can still signal failure in the v1 envelope.
      if (res.data.success === false) {
        throw new RepoError({
          code: null,
          message: res.data.description,
          status: null,
          response: res.data,
        })
      }

      return { id: res.data.data }
    } catch (err) {
      return throwRepoErrorFromApiV1Error(err)
    }
  }
}
